import { CircularProgress, Typography, List, ListItem, Box, Tooltip, IconButton, useTheme } from '@mui/material';
import React, { FC, useRef, useEffect, useCallback, useState } from 'react';
import { observer } from 'mobx-react';

import { FiExternalLink } from 'react-icons/fi';
import CloseIcon from '../icons/CloseIcon';

import { IPolicyPreviewItem } from '../../types/chat';
import { useAppStore } from '../../hooks/useAppStore';
import { useChatCategoryStore } from '../../hooks/useChatCategoryStore';
import { ChatCategoryFilters } from '../../stores/ChatCategoryStore';
import Row from '../ui/core/Row';
import { PolicyQuestionsFilters } from './policy-questions/PolicyQuestionsFilters';
import { useChatStore } from '../../hooks/useChatStore';
import useNotifier from '../../hooks/useNotifier';
import { apiService, Payer } from '../../services';
import { PreChatsHelper } from '../helpers/preChatsHelper';
import { PolicyTypeBadge } from './policy-questions/PolicyTypeBadge';

// Module-level cache: this panel mounts/unmounts every time it's opened/closed, so cache
// the payers list once fetched instead of refetching on every remount.
let cachedPayersData: Payer[] | null = null;

interface PoliciesPreviewTableProps {
    isLoading: boolean;
    policiesData: IPolicyPreviewItem[];
    total?: number;
    onLoadMore?: () => void;
    refetchData?: (search?: string) => void;
    isLoadingMore?: boolean;
    hasMoreData?: boolean;
}

export const PoliciesPreviewTable: FC<PoliciesPreviewTableProps> = observer(({ 
    isLoading, 
    policiesData,
    onLoadMore,
    refetchData,
    isLoadingMore = false, 
    hasMoreData = true 
}) => {
    const { setPolicyToPreview, policyToPreview } = useAppStore();
    const chatStore = useChatStore();
    const { setFilters, isSearching, setIsPoliciesDocumentsDrawerOpen, isAskingReveForPolicy } = useChatCategoryStore();
    const [loadingDocId, setLoadingDocId] = useState<string | null>(null);
    const [payersData, setPayersData] = useState<Payer[]>(cachedPayersData ?? []);
    const [isLoadingPayers, setIsLoadingPayers] = useState(!cachedPayersData);
    const notify = useNotifier();

    const observerRef = useRef<IntersectionObserver | null>(null);
    const selectedItemRef = useRef<HTMLLIElement | null>(null);
    const theme = useTheme();
    const OpenInNewIcon = FiExternalLink as any;

    // Mirrors PolicyQuestions.tsx's own payers fetch -- this panel can be mounted
    // alongside that page (side-by-side in the policy questions layout) without access
    // to its page-local payersData state, so it fetches its own copy of the same list,
    // skipping the fetch on remount once cachedPayersData is populated.
    useEffect(() => {
        if (cachedPayersData) {
            return;
        }

        const fetchPayers = async () => {
            let data: Payer[];
            try {
                setIsLoadingPayers(true);
                const payersResponse = await apiService.getPayers();

                if (payersResponse.success && payersResponse.data) {
                    data = payersResponse.data;
                } else {
                    console.error('Failed to fetch payers options:', payersResponse.error);
                    data = PreChatsHelper.buildFallbackPayers();
                }
            } catch (error) {
                console.error('Error fetching payers options:', error);
                data = PreChatsHelper.buildFallbackPayers();
            } finally {
                setIsLoadingPayers(false);
            }
            cachedPayersData = data!;
            setPayersData(data!);
        };

        fetchPayers();
    }, []);

    const onClose = () => {
        setIsPoliciesDocumentsDrawerOpen(false);
        // Clears policyToPreview too so ChatPanel's activePanel (isPoliciesDrawerOpen ||
        // policyToPreview) actually drops "policy" and the aside collapses in the chat
        // context -- isPoliciesDocumentsDrawerOpen alone only matters on /policy-questions.
        // The isAskingReveForPolicy guard only makes sense on /policy-questions itself,
        // where PolicyWelcomeContent's onOpenPanel can reopen the same policy -- nothing
        // ever resets the flag back to false once "Ask Reve" has navigated into a real
        // chat, so honoring it there permanently blocked this Close button from working.
        const isOnPolicyQuestionsPage = window.location.pathname.includes('policy-questions');
        if (!isAskingReveForPolicy || !isOnPolicyQuestionsPage) {
            setPolicyToPreview(null);
        }
    }

    const handleOpenInNewTab = async (e: React.MouseEvent, documentId: string, sourceUrl: string) => {
        e.stopPropagation();

        if(sourceUrl?.endsWith(".html")) {
            window.open(sourceUrl, '_blank', 'noopener,noreferrer');
            return;
        }

        // Open the tab synchronously, within the click gesture, so it isn't
        // popup-blocked once we go async below. Note: 'noopener' must NOT be
        // passed here — browsers return null instead of a window handle when
        // it's set, which would make the code below a no-op. We fully control
        // the navigation target (our own blob URL), so there's no tabnabbing
        // risk in keeping the opener reference.
        const newTab = window.open('', '_blank');

        try {
            setLoadingDocId(documentId);

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 8000);
            const response = await fetch(sourceUrl, { signal: controller.signal });
            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`Request failed with status ${response.status}`);
            }

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);

            if (newTab) {
                newTab.location.href = blobUrl;
            }
        } catch {
            newTab?.close();
            notify.showError('Unable to open this policy document — the source site is not responding. Please try again later.');
        } finally {
            setLoadingDocId(null);
        }
    };

    // isLoadingMore/hasMoreData/onLoadMore/isSearching/isLoading change on almost every render
    // during a fetch cycle (loading -> data appended -> loading done). Reading them via a ref
    // instead of a useCallback dep array keeps lastItemCallback's identity stable across those
    // renders, so React doesn't detach/reattach the observer (which re-fires an immediate
    // intersection check on the still-visible last row) on every render -- only when the DOM
    // node it's attached to actually changes. Without this, a single scroll-to-bottom cascaded
    // into a tight fetch loop that only stopped once every page had been loaded.
    const latestRef = useRef({ isLoadingMore, hasMoreData, onLoadMore, isSearching, isLoading });
    useEffect(() => {
        latestRef.current = { isLoadingMore, hasMoreData, onLoadMore, isSearching, isLoading };
    }, [isLoadingMore, hasMoreData, onLoadMore, isSearching, isLoading]);

    // Setup IntersectionObserver for lazy loading
    const lastItemCallback = useCallback((node: HTMLLIElement | null) => {
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }

        if (!node) return;

        observerRef.current = new IntersectionObserver((entries) => {
            const { isLoadingMore, hasMoreData, onLoadMore, isSearching, isLoading } = latestRef.current;
            if (isLoadingMore || isSearching || isLoading) return;
            if (entries[0].isIntersecting && hasMoreData && onLoadMore) {
                onLoadMore();
            }
        }, { threshold: 0.1 });

        observerRef.current.observe(node);
    }, []);

    // Cleanup observer on unmount
    useEffect(() => {
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);

    // Scroll the previewed policy into view -- runs whenever the panel opens with a
    // policy already selected (e.g. via PolicyQuestionsTable's "Ask about this policy")
    // or the selection otherwise changes, so the highlighted row isn't left off-screen.
    // Deliberately excludes policiesData: appendPolicyResearchData creates a new array
    // reference on every "load more" page fetch, and depending on it here would yank the
    // user's scroll position back to the selected row on each pagination step.
    useEffect(() => {
        if (policyToPreview?.document_id) {
            selectedItemRef.current?.scrollIntoView({ block: 'center' });
        }
    }, [policyToPreview?.document_id]);

    // State/payer changes don't carry their own cascading reset logic here (unlike
    // PolicyQuestions.tsx's handleChange) -- this panel just applies the field and refetches.
    const onFormChange = (field: keyof ChatCategoryFilters, value: string) => {
        setFilters({ [field]: value });
        refetchData?.();
    }

    return (
       <Box sx={{ backgroundColor: "#F8FAFC",  display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <Row
                id='header'
                sx={{
                    alignItems: 'center',
                    gap: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    height: 'auto',
                    flexShrink: 0,
                    width: '100%',
                    background: 'white'
                }}
            >
               <Row sx={{ px: 2.5, py: 1, gap: 2, alignItems: 'center', flex: 1, width: '100%', minWidth: 0 }}>
                    <Typography
                        variant='body1'
                        sx={{
                            fontWeight: 700,
                            color: 'text.primary',
                            fontSize: '1rem',
                        }}
                    >
                        Policies
                    </Typography>

                    <IconButton
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        size="small"
                        sx={{
                            flexShrink: 0,
                            ml: 'auto',
                            color: theme.palette.text.secondary,
                            '&:hover': {
                                color: theme.palette.text.primary,
                                backgroundColor: theme.palette.action.hover,
                            },
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                '& svg': { width: 20, height: 20 },
                            }}
                        >
                            <CloseIcon className="" />
                        </Box>
                    </IconButton>
               </Row>
            </Row>

            <Row
                id="search-controls"
                sx={{
                    width: '100%',
                    height: 'auto',
                    flexShrink: 0,
                    backgroundColor: "#F8FAFC", 
                }}
            >
                <PolicyQuestionsFilters
                    panelMode
                    payersData={payersData}
                    isLoadingPayers={isLoadingPayers || isSearching || isLoading}
                    onFormChange={onFormChange}
                />
            </Row>

            <List id="data-list"
                sx={{
                    overflow: 'hidden',
                    overflowY: 'auto',
                    flex: 1,
                    width: '100%',
                    margin: 'auto',
                    p: 0,
                    mx: 1,
                    my: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '10px',
                    ...(isSearching || isLoading ? { opacity: 0.8, pointerEvents: 'none' } : {}),
                }}
                >
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) :
                !policiesData || policiesData?.length === 0 ? (
                    <Typography variant='body1' sx={{ marginTop: 8, marginLeft: 2, marginRight: 2, textAlign: 'center' }}>
                        No policies for selected date of service are available, please check the date of service.
                    </Typography>
                ) :
                policiesData.map((policy, index) => {
                    const isSelected = policyToPreview?.document_id === policy.document_id;
                    const isLastItem = index === policiesData.length - 1;

                    // Re-created each render so React detaches the previous render's ref
                    // (calling it with `node: null` against its own closed-over `isSelected`)
                    // before attaching this one -- correctly clearing selectedItemRef when
                    // this row stops being the selected one.
                    const setItemRef = (node: HTMLLIElement | null) => {
                        if (isSelected) {
                            selectedItemRef.current = node;
                        }
                        if (isLastItem) {
                            lastItemCallback(node);
                        }
                    };

                    return (
                        <ListItem
                            key={policy.document_id}
                            ref={isSelected || isLastItem ? setItemRef : null}
                            onClick={() => {
                                setPolicyToPreview(isSelected ? null : policy)
                                chatStore.setPreviewFileChanged(true);
                            }}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                px: 2,
                                py: 1.25,
                                minHeight: 'auto',
                                cursor: 'pointer',
                                transition: 'background-color 0.15s ease',
                                backgroundColor: isSelected ? 'custom.selectedRowBackground' : 'background.paper',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                fontWeight: isSelected ? "600" : "normal",
                                color: isSelected ? "custom.brandTeal" : "text.primary",
                                '&:last-of-type': {
                                    borderBottom: 'none',
                                },
                                '&:hover': {
                                    fontWeight: "normal",
                                    backgroundColor: isSelected ? 'custom.selectedRowBackground' : 'grey.50',
                                    color: "text.primary"
                                }
                            }}
                        >
                            <PolicyTypeBadge sourceUrl={policy.source_url} />

                            {/* Title */}
                            <Typography
                                variant='body1'
                                noWrap
                                sx={{
                                    flex: 1,
                                    lineHeight: 1.4,
                                    color: 'inherit',
                                    fontWeight: 'inherit',
                                }}
                            >
                                {policy.title}
                            </Typography>

                            {
                                loadingDocId === policy.document_id ? 
                                <IconButton 
                                    size='small' 
                                    disabled
                                    onClick={(e) => handleOpenInNewTab(e, policy.document_id, policy.source_url)}
                                >
                                    <CircularProgress size={14} />
                                </IconButton> : 
                                <Tooltip 
                                    arrow
                                    placement='right'
                                    title='View Policy'
                                    disableHoverListener={loadingDocId === policy.document_id}
                                >
                                    <IconButton 
                                        size='small' 
                                        onClick={(e) => handleOpenInNewTab(e, policy.document_id, policy.source_url)}
                                    >
                                        <OpenInNewIcon size={14} />
                                    </IconButton>
                                </Tooltip>
                                }
                            
                        </ListItem>
                    )
                })}
                {isLoadingMore && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                )}
            </List>
       </Box>
    )
})